import { DocsSiteSegment, SharedSegment } from '@/shared/constant/DocSegment';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import type {
  DocsIdTypeInfo,
  DocsSiteSummaryConfig,
  NavParamsInfo,
  SEOInfoData,
} from '@/shared/constant/resource-path/ResourcePathDefs';
import type { TypeInRoute } from '@/shared/constant/TypeInRoute';
import { generateUrlQueryString } from '@/shared/request/generateUrlQueryString';
import { slugify } from '@/shared/util/slugify';
import {
  typeInRouteToRelatedType,
  typeInRouteToUrlResourceKeyTypeAbbr,
} from '@/shared/util/typeTransform';
import { URL_DEFAULT_RULES, URL_RESOURCE_KEY_STANDARD } from '@tarslib/types/lib/projectSetting';
import trim from 'lodash-es/trim';
import trimEnd from 'lodash-es/trimEnd';
import type { ItemPathPrefix } from '../ItemPathPrefix';

export interface ResourceParams {
  name: string;
  id: number;
  type: TypeInRoute;
}

export interface BuildResourceConfig {
  docsIdTypeData: DocsIdTypeInfo;
  itemPathPrefix: ItemPathPrefix;
  seoInfos: Map<string, SEOInfoData>;
  docsSiteSummary: DocsSiteSummaryConfig;
}

/**
 * Builds a resource path based on provided configuration and parameters
 * @param config The configuration for building resource paths
 * @param params Resource parameters including name, id, and type
 * @param navParams Navigation parameters
 * @returns A formatted resource path string
 */
export function buildResourcePath(
  config: BuildResourceConfig,
  params: ResourceParams | undefined,
  navParams: NavParamsInfo | undefined,
  options?: {
    urlSearchParametersObject?: Record<string, any>;
    suffixPath?: string;
    prefixPath?: string;
  },
): string {
  let resourcePath = '';
  let isEndWithSlash = false;
  const navId = getNavId(config, navParams);

  if (params) {
    const pathInfo = buildResourcePathInternal(config, params);
    resourcePath = pathInfo.path;
    isEndWithSlash = pathInfo.isEndWithSlash;
  }

  const path = spliceItemPathPrefix(config, '/' + resourcePath);

  let queries = Object.entries(options?.urlSearchParametersObject ?? {}).map(([key, value]) => ({
    key,
    value,
  }));

  if (navId) {
    queries = [
      {
        key: 'nav',
        value: navId,
      },
      ...queries,
    ];
  }
  const newPath = (() => {
    const suffixPath = options?.suffixPath ?? '';
    const prefixPath = options?.prefixPath ?? '';

    return `${prefixPath}${trimEnd(path, '/')}${isEndWithSlash ? '/' : ''}${suffixPath}` || '/';
  })();

  if (queries.length > 0) {
    const query = generateUrlQueryString(queries);
    return `${newPath}${query}`;
  }
  return newPath;
}

function spliceItemPathPrefix(config: BuildResourceConfig, resourcePath: string): string {
  const list: string[] =
    config.docsIdTypeData.onlineType === OnlineType.SharedDoc &&
    config.itemPathPrefix.isSharedDocIndependentDomain
      ? []
      : [config.itemPathPrefix.baseUrl];
  if (config.docsIdTypeData.onlineType === OnlineType.DocsSite) {
    if (config.docsIdTypeData.visitType === DocsSiteVisitType.CustomDomain) {
      list.push(config.itemPathPrefix.subdirectory);
    } else {
      list.push(DocsSiteSegment);
      list.push(config.docsIdTypeData.onlineId.toString());
    }

    const versionSlug = config.itemPathPrefix.versionSlug;
    if (versionSlug) {
      list.push(versionSlug);
    }
  } else {
    if (!config.itemPathPrefix.isSharedDocIndependentDomain) {
      list.push(SharedSegment);
    }
    list.push(config.docsIdTypeData.onlineId.toString());
  }
  list.push(resourcePath);

  const path = list
    .map((x) => trim(String(x).trim(), '/').trim())
    .filter((x) => !!x)
    .join('/');

  return '/' + path;
}

function getNavId(config: BuildResourceConfig, navParams: NavParamsInfo | undefined) {
  if (!navParams?.navId) {
    return undefined;
  }

  const { folderIdMapUsage, parentId, navId } = navParams;
  const value = [...(folderIdMapUsage.get(parentId) ?? [])];

  if (value.length < 2) {
    return undefined;
  }

  if (value[0] === navId) {
    return undefined;
  }

  return navId;
}

function buildResourcePathInternal(
  config: BuildResourceConfig,
  params: ResourceParams,
): {
  path: string;
  isEndWithSlash: boolean;
} {
  const { name, id, type } = params;

  const relatedType = typeInRouteToRelatedType(type);
  const key = `${id}:${relatedType}`;

  const customSEOInfo = config.seoInfos.get(key);

  if (customSEOInfo && customSEOInfo.path) {
    return {
      path: trim(customSEOInfo.path, '/'),
      isEndWithSlash: customSEOInfo.path.length > 1 && customSEOInfo.path.endsWith('/'),
    };
  }

  const { resourceKeyStandard, defaultRule } =
    config.docsSiteSummary.summary.projectSetting?.advancedSettings?.publishedDocUrlRules ?? {};

  return {
    path: generateResourcePath(name, id, type, resourceKeyStandard, defaultRule),
    isEndWithSlash: false,
  };
}

function generateResourcePath(
  name: string,
  id: number,
  type: TypeInRoute,
  resourceKeyStandard?: string,
  defaultRule?: string,
): string {
  let resourceName = generateResourceName(name, defaultRule);
  const resourceKey = generateResourceKey(id, type, resourceKeyStandard);

  // If resourceKey assigns resourceName to empty string in the special case,
  // we need to respect that in our result
  if (
    resourceKeyStandard === URL_RESOURCE_KEY_STANDARD.NEW &&
    !typeInRouteToUrlResourceKeyTypeAbbr(type)
  ) {
    resourceName = '';
  }

  return `${resourceName}${resourceKey}`;
}

function generateResourceName(name: string, defaultRule?: string): string {
  if (defaultRule === URL_DEFAULT_RULES.SLUGIFY_WITH_RESOURCE_KEY && name) {
    return slugify(name) + '-';
  }

  return '';
}

function generateResourceKey(id: number, type: TypeInRoute, resourceKeyStandard?: string): string {
  const resourceKeyTypeAbbr = typeInRouteToUrlResourceKeyTypeAbbr(type);

  if (resourceKeyStandard === URL_RESOURCE_KEY_STANDARD.NEW) {
    if (resourceKeyTypeAbbr) {
      return `${id}${resourceKeyTypeAbbr}`;
    }
  }

  return trim(`${type}-${id}`, '-');
}
